import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import uniqBy from 'lodash.uniqby';
import { useLocations } from './useLocations';

export const useSurveysOptions = () => {
    const surveys = useSelector(state => state.surveys.data);

    const { locationsMap } = useLocations();

    const getSurveysByLocationIds = useCallback(
        locationsIds => {
            return [
                ...new Set(
                    locationsIds
                        .map(id => locationsMap[id])
                        .filter(Boolean)
                        .map(company => company.surveys)
                        .flat()
                        .map(survey => survey.id),
                ),
            ];
        },
        [locationsMap],
    );

    const mapSurveyPropsToOption = survey => ({
        value: survey.id,
        label: survey.name,
        message: survey.message,
        emailSubject: survey.emailSubject,
        sendSurveyEnabled: survey.sendSurveyEnabled,
    });

    const getSurveysOptionsByLocation = useCallback(
        (locationId, propsMapper = mapSurveyPropsToOption) => {
            const location = locationsMap[locationId];
            if (!location) return [];

            const surveys = location.surveys.map(propsMapper);
            return uniqBy(surveys, 'value');
        },
        [locationsMap],
    );

    const getSurveysOptions = useCallback(() => {
        return uniqBy(
            surveys.map(item => mapSurveyPropsToOption(item.survey)),
            'value',
        );
    }, [surveys]);

    const getBrandSurveysOptionsByIds = useCallback(
        (locationsIds, propsMapper = mapSurveyPropsToOption) => {
            return uniqBy(
                locationsIds
                    .map(id => locationsMap[id])
                    .filter(Boolean)
                    .map(company => company.surveys)
                    .flat()
                    .map(propsMapper),
                'value',
            );
        },
        [locationsMap],
    );

    const getBrandSurveysOptionsByBrandId = useCallback(
        (brandId, propsMapper = mapSurveyPropsToOption) => {
            if (!brandId) return [];
            return uniqBy(
                Object.values(locationsMap)
                    .map(company => company.surveys)
                    .flat()
                    .filter(survey => survey.brandId === brandId && !survey.locationId)
                    .map(propsMapper),
                'value',
            );
        },
        [locationsMap],
    );

    return {
        getBrandSurveysOptionsByIds,
        getBrandSurveysOptionsByBrandId,
        getSurveysOptionsByLocation,
        getSurveysByLocationIds,
        getSurveysOptions,
    };
};
